import React, { useEffect, useRef, useState } from 'react';
import { Alert, Autocomplete, Box, Button, CircularProgress, TextField, createFilterOptions } from '@mui/material';
import { fetchModelCatalog } from '../../Utils/adminSettingsApi';

export const modelCatalogProviders = new Set(['openai', 'openrouter', 'vllm']);

const filterModels = createFilterOptions({
  limit: 100,
  stringify: (model) => `${model.id} ${model.name}`,
});

const ModelNameInput = ({ formData, onChange, open, t }) => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const requestSequence = useRef(0);
  const { id, provider, model_type: modelType, api_endpoint: endpoint, api_token: token } = formData;

  useEffect(() => {
    requestSequence.current += 1;
    setModels([]);
    setLoading(false);
    setError('');
    setLoaded(false);
    return () => { requestSequence.current += 1; };
  }, [id, provider, modelType, endpoint, token, open]);

  const loadModels = async () => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    setError('');
    try {
      const response = await fetchModelCatalog({
        id, provider, model_type: modelType, api_endpoint: endpoint, api_token: token,
      });
      if (sequence !== requestSequence.current) return;
      setModels(Array.isArray(response?.models) ? response.models : []);
      setLoaded(true);
    } catch (failure) {
      if (sequence !== requestSequence.current) return;
      setError(failure?.message || t('global_setting.model_catalog_error'));
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  };

  const changeValue = (value) => onChange({ target: { value } });
  return (
    <Box>
      <Autocomplete
        freeSolo
        options={models}
        value={formData.model_name || ''}
        inputValue={formData.model_name || ''}
        getOptionLabel={(option) => typeof option === 'string' ? option : option.id}
        isOptionEqualToValue={(option, value) => option.id === (typeof value === 'string' ? value : value.id)}
        filterOptions={filterModels}
        onChange={(_, value) => changeValue(typeof value === 'string' ? value : value?.id || '')}
        onInputChange={(_, value, reason) => {
          if (reason === 'input' || reason === 'clear') changeValue(value);
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {option.name === option.id ? option.id : `${option.name} (${option.id})`}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('global_setting.model_name', 'Model Name')}
            placeholder={t('global_setting.model_name_placeholder')}
            helperText={loaded && !models.length
              ? t('global_setting.model_catalog_empty') : t('global_setting.model_catalog_hint')}
          />
        )}
      />
      <Button size="small" disabled={loading} onClick={loadModels}
        startIcon={loading ? <CircularProgress size={14} /> : undefined}>
        {t(loading ? 'global_setting.model_catalog_loading' : 'global_setting.model_catalog_load')}
      </Button>
      {error && <Alert severity="warning">{t('global_setting.model_catalog_error')} {error}</Alert>}
    </Box>
  );
};

export default ModelNameInput;
